import { useEffect, useRef } from "react";
import {
  Animated,
  ImageBackground,
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  ViewStyle,
  TextStyle,
  ImageStyle,
} from "react-native";
import { useDevice } from "../../../utils/hooks/useDevice";
import Toast from "react-native-toast-message";

type SvaraModalProps = {
  visible: boolean;
  onClose: () => void;
  titleImage?: any;
  backgroundImage?: any;
  closeIcon: any;
  children?: React.ReactNode;
  closeButtonSize?: "small" | "big";
  closeButtonStyles?: ViewStyle;
  nativeCloseButtonStyles?: ViewStyle;
  containerStyles?: ViewStyle;
  contentWrapperStyles?: ViewStyle;
  titleImageStyle?: ImageStyle;
  overlayStyle?: ViewStyle;
};

const SvaraModal = ({
  visible,
  onClose,
  titleImage,
  backgroundImage,
  titleImageStyle,
  closeIcon,
  children,
  closeButtonSize = "small",
  closeButtonStyles,
  nativeCloseButtonStyles,
  containerStyles,
  contentWrapperStyles,
  overlayStyle,
}: SvaraModalProps) => {
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.timing(opacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  const handleClose = () => {
    Animated.timing(opacity, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(onClose);
  };
  const { isMobile } = useDevice();

  const computedTitleStyle = Array.isArray(titleImageStyle)
    ? titleImageStyle
    : [titleImageStyle];

  const finalWidth =
    (titleImageStyle as any)?.width ?? styles.titleImage.width ?? 340;

  return (
    <Modal animationType="none" transparent visible={visible}>
      <Animated.View style={{ ...styles.overlay, ...overlayStyle, opacity }}>
        <Toast position="top" topOffset={60} />
        <ImageBackground
          source={backgroundImage}
          resizeMode="stretch"
          style={{
            borderColor: "white",
            borderWidth: 11,
            ...styles.modalBackground,
            ...containerStyles,
            backgroundColor: backgroundImage
              ? "none"
              : styles.modalBackground.backgroundColor,
          }}
        >
          <View style={styles.modalContainer}>
            {/* Optional Title Image */}
            {titleImage && (
              <Image
                source={titleImage}
                resizeMode="contain"
                style={[
                  styles.titleImage,
                  titleImageStyle,
                  {
                    transform: [{ translateX: -(Number(finalWidth) || 0) / 2 }],
                  },
                ]}
              />
            )}

            {/* Close Button */}
            <TouchableOpacity
              onPress={handleClose}
              activeOpacity={0.7}
              style={[
                styles.closeButton,
                closeButtonSize === "big"
                  ? styles.bigCloseButton
                  : styles.smallCloseButton,
                closeButtonStyles,
              ]}
            >
              <Image source={closeIcon} style={styles.closeIcon} />
            </TouchableOpacity>

            {/* Main Content */}
            <View style={[styles.content, contentWrapperStyles]}>
              {children}
            </View>
          </View>
        </ImageBackground>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create<{
  overlay: ViewStyle;
  modalBackground: ViewStyle;
  modalContainer: ViewStyle;
  titleImage: ImageStyle;
  closeButton: ViewStyle;
  smallCloseButton: ViewStyle;
  bigCloseButton: ViewStyle;
  closeIcon: ImageStyle;
  content: ViewStyle;
  modalTitle: TextStyle;
  connectText: TextStyle;
}>({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.7)",
  },
  modalBackground: {
    maxWidth: "96%",
    maxHeight: "90%",
    backgroundColor: "rgba(17, 35, 69, 0.8)",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#00f0ff",
    padding: 2,
    paddingTop: 0,
  },

  modalContainer: {
    width: "100%",
    maxHeight: "100%",
    position: "relative",
    paddingTop: 0,
  },
  titleImage: {
    width: 340,

    height: 120,
    position: "absolute",
    top: -70,
    left: "50%",
  },
  closeButton: {
    position: "absolute",
    zIndex: 100,
  },
  nativeCloseButton: {
    width: 24,
    height: 24,
    top: 10,
    right: 12,
  },
  smallCloseButton: {
    width: 22,
    height: 22,
    top: 10,
    right: 10,
  },
  bigCloseButton: {
    width: 48,
    height: 48,
    top: -26,
    right: 8,
  },
  closeIcon: {
    width: "100%",
    height: "100%",
  },
  content: {
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 30,
    gap: 20,
  },
  modalTitle: {
    color: "#fff",
    fontSize: 22,
    fontWeight: "bold",
  },
  connectText: {
    color: "#ccc",
    fontSize: 16,
    textAlign: "center",
  },
});

export default SvaraModal;
